import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, Wrench, Clock, CheckCircle2, XCircle, Play, Pause,
  PackageCheck, Truck, DollarSign, Shield, FileText, Upload, Trash2,
  Paperclip, ListChecks, User as UserIcon, Calendar, Copy, ExternalLink,
  Printer, Receipt, Sparkles, X, Plus, Search, Pencil, RotateCcw, MessageCircle, Loader2, Monitor, Bike,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDateTime } from '@/lib/api'
import { useFilial } from '@/lib/filial'
import { pedirExclusao } from '@/lib/confirmarExclusao'

const STATUS_COR: Record<string, string> = {
  rascunho:               'bg-gray-100 text-gray-700',
  aguardando_diagnostico: 'bg-amber-100 text-amber-800',
  aguardando_aprovacao:   'bg-orange-100 text-orange-800',
  aprovada:               'bg-blue-100 text-blue-800',
  em_execucao:            'bg-indigo-100 text-indigo-800',
  aguardando_peca:        'bg-yellow-100 text-yellow-800',
  concluida:              'bg-teal-100 text-teal-800',
  entregue:               'bg-cyan-100 text-cyan-800',
  faturada:               'bg-emerald-100 text-emerald-800',
  encerrada:              'bg-emerald-50 text-emerald-700',
  em_garantia:            'bg-rose-100 text-rose-800',
  reprovada:              'bg-red-100 text-red-700',
  cancelada:              'bg-red-100 text-red-700',
}

const STATUS_ITEM_META: Record<string, { label: string; cor: string }> = {
  pendente:  { label: 'Pendente',  cor: 'bg-amber-100 text-amber-800' },
  aprovado:  { label: 'Aprovado',  cor: 'bg-blue-100 text-blue-800' },
  concluido: { label: 'Concluído', cor: 'bg-emerald-100 text-emerald-800' },
  reprovado: { label: 'Reprovado', cor: 'bg-red-100 text-red-700' },
  cancelado: { label: 'Cancelado', cor: 'bg-gray-200 text-gray-600' },
}

const EVENTO_LABEL: Record<string, string> = {
  abertura: 'Abertura',
  diagnostico: 'Diagnóstico iniciado',
  orcamento_enviado: 'Orçamento enviado',
  aprovacao: 'Aprovação do cliente',
  reprovacao: 'Reprovada pelo cliente',
  inicio_execucao: 'Execução iniciada',
  aguardando_peca: 'Aguardando peça',
  retomada: 'Retomada',
  conclusao: 'Concluída',
  entrega: 'Entregue',
  faturamento: 'Faturada',
  encerramento: 'Encerrada',
  cancelamento: 'Cancelada',
  reversao: 'Voltou etapa',
  garantia_aberta: 'Garantia aberta',
  reabertura: 'Reaberta para aprovação',
  item_aprovado: 'Item aprovado',
  item_reprovado: 'Item reprovado',
  assinatura_abertura: 'Assinatura (abertura)',
  assinatura_entrega: 'Assinatura (entrega)',
}

// Transições permitidas por status (espelha backend)
const ACOES: Record<string, { key: string; label: string; icon: any; cor?: string }[]> = {
  rascunho: [
    { key: 'enviar-aprovacao', label: 'Enviar p/ Aprovação', icon: FileText },
    // 'cancelar' saiu daqui: agora é um botão dedicado "Cancelar OS" no cabeçalho,
    // visível em TODOS os status ativos (antes só aparecia no rascunho).
  ],
  aguardando_diagnostico: [
    { key: 'enviar-aprovacao', label: 'Enviar p/ Aprovação', icon: FileText },
  ],
  aguardando_aprovacao: [
    { key: 'aprovar', label: 'Aprovar', icon: CheckCircle2, cor: 'bg-emerald-600 text-white' },
    { key: 'reprovar', label: 'Reprovar', icon: XCircle, cor: 'bg-red-50 text-red-700' },
  ],
  aprovada: [
    { key: 'iniciar', label: 'Iniciar Execução', icon: Play, cor: 'bg-indigo-600 text-white' },
    { key: 'reabrir', label: 'Voltar p/ Aprovação', icon: RotateCcw },
  ],
  em_execucao: [
    { key: 'aguardar-peca', label: 'Aguardar Peça', icon: Pause },
    { key: 'concluir', label: 'Concluir', icon: PackageCheck, cor: 'bg-teal-600 text-white' },
  ],
  aguardando_peca: [
    { key: 'retomar', label: 'Retomar', icon: Play, cor: 'bg-indigo-600 text-white' },
  ],
  concluida: [
    { key: 'entregar', label: 'Entregar', icon: Truck, cor: 'bg-cyan-600 text-white' },
  ],
  entregue: [
    { key: 'faturar', label: 'Faturar', icon: DollarSign, cor: 'bg-emerald-600 text-white' },
    { key: 'garantia', label: 'Abrir Garantia', icon: Shield },
  ],
  faturada: [
    { key: 'encerrar', label: 'Encerrar', icon: CheckCircle2 },
    { key: 'garantia', label: 'Abrir Garantia', icon: Shield },
  ],
  encerrada: [
    { key: 'garantia', label: 'Abrir Garantia', icon: Shield },
  ],
  reprovada: [
    { key: 'reabrir', label: 'Reabrir p/ Aprovação', icon: RotateCcw, cor: 'bg-indigo-600 text-white' },
  ],
}

export default function OSDetalhePage() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const qc = useQueryClient()
  // Abre já na aba de Itens (fluxo principal ao atender a OS); Dados fica a um clique.
  const [tab, setTab] = useState<'dados' | 'itens' | 'timeline' | 'anexos' | 'portal'>('itens')
  const [histCompleto, setHistCompleto] = useState(false)  // timeline: resumo (3 marcos) x detalhado
  const [iaSugestao, setIaSugestao] = useState<any>(null)
  const EMPTY_ITEM = { produto_id: null, descricao: '', quantidade: 1, preco_unit: 0, desconto: 0, origem_mp: 'estoque_proprio', garantia_dias: 0 }
  const [novoItem, setNovoItem] = useState<any>(EMPTY_ITEM)
  const [buscaProd, setBuscaProd] = useState('')
  const [abrirBusca, setAbrirBusca] = useState(false)
  const [editDados, setEditDados] = useState(false)
  const [formDados, setFormDados] = useState<any>({})
  const [waOpen, setWaOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [editItemId, setEditItemId] = useState<number | null>(null)
  const [editItem, setEditItem] = useState<any>({})
  const [editBuscaProd, setEditBuscaProd] = useState('')
  const [editAbrirBusca, setEditAbrirBusca] = useState(false)
  // FEATURE 06/07: modal de faturamento — à vista vai pro PDV (entra no CAIXA);
  // a prazo gera título a receber. Antes "Faturar" sempre criava título (não caía no caixa).
  const [showFaturar, setShowFaturar] = useState(false)
  const [fatForma, setFatForma] = useState('boleto')   // a prazo: boleto|crediario|prazo
  const [fatVenc, setFatVenc] = useState('')

  const { data: os, isLoading, refetch } = useQuery<any>({
    queryKey: ['os', id],
    queryFn: async () => (await api.get(`/servicos/${id}`)).data,
    enabled: !!id,
  })

  // Carrega o cliente da OS direto por id (a base tem milhares — buscar a lista
  // inteira capava e o cliente certo não vinha → telefone/nome em branco).
  const { data: cliente } = useQuery<any>({
    queryKey: ['os-cliente', os?.cliente_id],
    queryFn: async () => (await api.get(`/cadastros/pessoas/${os!.cliente_id}`)).data,
    enabled: !!os?.cliente_id,
  })

  const transicao = useMutation({
    mutationFn: async (arg: string | { acao: string; observacao?: string }) => {
      const acao = typeof arg === 'string' ? arg : arg.acao
      const body: any = acao === 'faturar' ? { forma_pagamento: os?.forma_pagamento || 'pix' } : {}
      if (typeof arg !== 'string' && arg.observacao !== undefined) body.observacao = arg.observacao
      return (await api.post(`/servicos/${id}/${acao}`, body)).data
    },
    onSuccess: () => {
      toast.success('Ação realizada')
      qc.invalidateQueries({ queryKey: ['os', id] })
      qc.invalidateQueries({ queryKey: ['ordens-servico'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  // FEATURE 06/07: faturar A PRAZO — gera título a receber (Contas a Receber). À vista
  // NÃO passa por aqui: vai pro PDV (cria pedido → cai no caixa/movimentação).
  const faturarAPrazo = useMutation({
    mutationFn: async () => (await api.post(`/servicos/${id}/faturar`, {
      forma_pagamento: fatForma,
      vencimento: fatVenc || undefined,
    })).data,
    onSuccess: () => {
      toast.success('OS faturada — título gerado em Contas a Receber')
      setShowFaturar(false)
      qc.invalidateQueries({ queryKey: ['os', id] })
      qc.invalidateQueries({ queryKey: ['ordens-servico'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao faturar'),
  })

  // "Excluir" a OS = ARQUIVAR (soft-delete no backend, DELETE /servicos/{id}):
  // some das listas do dia a dia mas continua no sistema. Usado pra tirar da
  // tela OS de teste/erradas ou já encerradas, sem apagar o histórico.
  const arquivar = useMutation({
    mutationFn: async () => (await api.delete(`/servicos/${id}`)).data,
    onSuccess: () => {
      toast.success('OS excluída (arquivada)')
      qc.invalidateQueries({ queryKey: ['ordens-servico'] })
      nav('/servicos')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })

  const iaDiag = useMutation({
    mutationFn: async () => (await api.post(`/servicos/${id}/ia-diagnostico`)).data,
    onSuccess: (r: any) => setIaSugestao(r),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Sofia indisponível'),
  })

  const aplicarIa = useMutation({
    mutationFn: async () => (await api.put(`/servicos/${id}`, {
      diagnostico: iaSugestao?.diagnostico || undefined,
      laudo_tecnico: iaSugestao?.laudo_tecnico || undefined,
      checklist: (iaSugestao?.checklist?.length ? iaSugestao.checklist.map((t: string) => ({ texto: t, ok: false })) : undefined),
    })).data,
    onSuccess: () => { toast.success('Diagnóstico aplicado'); setIaSugestao(null); refetch() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao aplicar'),
  })

  const podeNfse = os && (os.status === 'faturada' || os.status === 'encerrada')
  const { data: nfse } = useQuery<any>({
    queryKey: ['os-nfse', id],
    queryFn: async () => (await api.get(`/servicos/${id}/nfse`)).data,
    enabled: !!podeNfse,
  })

  const emitirNfse = useMutation({
    mutationFn: async () => (await api.post(`/servicos/${id}/nfse`, {})).data,
    onSuccess: (r: any) => {
      if (r?.status === 'autorizado') toast.success(`NFS-e ${r.numero || ''} emitida`)
      else if (r?.status === 'processando') toast('NFS-e em processamento na prefeitura')
      else toast.error(r?.mensagem || 'Falha na emissão')
      qc.invalidateQueries({ queryKey: ['os-nfse', id] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao emitir NFS-e'),
  })

  const cancelarNfse = useMutation({
    mutationFn: async (justificativa: string) =>
      (await api.post(`/servicos/${id}/nfse/cancelar`, { justificativa })).data,
    onSuccess: () => { toast.success('NFS-e cancelada'); qc.invalidateQueries({ queryKey: ['os-nfse', id] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao cancelar'),
  })

  async function abrirNfsePdf() {
    try {
      const r = await api.get(`/servicos/${id}/nfse/pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch { toast.error('Erro ao abrir NFS-e') }
  }

  const aprovarItem = useMutation({
    mutationFn: async (itemId: number) =>
      (await api.post(`/servicos/${id}/itens/${itemId}/aprovar`)).data,
    onSuccess: () => {
      toast.success('Item aprovado')
      refetch()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const reprovarItem = useMutation({
    mutationFn: async (itemId: number) =>
      (await api.post(`/servicos/${id}/itens/${itemId}/reprovar`)).data,
    onSuccess: () => {
      toast.success('Item reprovado')
      refetch()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const removerItem = useMutation({
    mutationFn: async (itemId: number) =>
      (await api.delete(`/servicos/${id}/itens/${itemId}`)).data,
    onSuccess: () => { toast.success('Item removido'); refetch() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const atualizarItem = useMutation({
    mutationFn: async () => (await api.put(`/servicos/${id}/itens/${editItemId}`, {
      produto_id: editItem.produto_id,
      descricao: editItem.descricao || undefined,
      quantidade: Number(editItem.quantidade) || 0,
      preco_unit: Number(editItem.preco_unit) || 0,
      desconto: Number(editItem.desconto) || 0,
      origem_mp: editItem.origem_mp,
      garantia_dias: Number(editItem.garantia_dias) || 0,
    })).data,
    onSuccess: () => { toast.success('Item atualizado'); setEditItemId(null); refetch() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao atualizar item'),
  })

  // Item é editável se a OS está aberta E o item ainda não foi fechado.
  const osAberta = !['faturada', 'encerrada', 'reprovada', 'cancelada'].includes(os?.status)
  // FEATURE 06/07: status de garantia do item — começa a valer na conclusão da OS
  // (fallback entrega/faturamento) e vale garantia_dias. null = ainda não iniciou.
  const garantiaItem = (it: any) => {
    const inicioStr = os?.data_conclusao || os?.data_entrega || os?.data_faturamento
    if (!it?.garantia_dias || !inicioStr) return null
    const fim = new Date(inicioStr); fim.setDate(fim.getDate() + Number(it.garantia_dias))
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0)
    const dias = Math.ceil((fim.getTime() - hoje.getTime()) / 86400000)
    return { vigente: dias >= 0, dias, ate: fim.toLocaleDateString('pt-BR') }
  }
  const itemEditavel = (it: any) =>
    osAberta && !['concluido', 'reprovado', 'cancelado'].includes(it.status_item)
  const iniciarEdicaoItem = (it: any) => {
    setEditItemId(it.id)
    setEditItem({
      produto_id: it.produto_id,
      descricao: it.descricao || '',
      quantidade: Number(it.quantidade) || 0,
      preco_unit: Number(it.preco_unit) || 0,
      desconto: Number(it.desconto) || 0,
      origem_mp: it.origem_mp || 'estoque_proprio',
      garantia_dias: Number(it.garantia_dias) || 0,
    })
    setEditBuscaProd(it.descricao || '')
    setEditAbrirBusca(false)
  }

  const removerAnexo = useMutation({
    mutationFn: async (anexoId: number) =>
      (await api.delete(`/servicos/${id}/anexos/${anexoId}`)).data,
    onSuccess: () => { toast.success('Anexo removido'); refetch() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const { data: produtosBusca = [] } = useQuery<any[]>({
    queryKey: ['os-add-produtos', buscaProd],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { q: buscaProd.trim() || undefined, limit: 20 } })).data,
    enabled: abrirBusca,
  })

  const { data: produtosBuscaEdit = [] } = useQuery<any[]>({
    queryKey: ['os-edit-produtos', editBuscaProd],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { q: editBuscaProd.trim() || undefined, limit: 20 } })).data,
    enabled: editAbrirBusca,
  })

  const filialAtualId = useFilial(s => s.filialAtual?.id ?? null)
  const { data: tecnicos = [] } = useQuery<any[]>({
    queryKey: ['tecnicos-os', filialAtualId],
    queryFn: async () => (await api.get('/cadastros/colaboradores', { params: { tipo: 'tecnico', ativos: true, filial: true } })).data,
  })

  const [showBikeModal, setShowBikeModal] = useState(false)

  const vincularBike = useMutation({
    mutationFn: async (equipamentoId: number | null) =>
      (await api.put(`/servicos/${id}`, { equipamento_id: equipamentoId })).data,
    onSuccess: () => { toast.success('Bike atualizada'); setShowBikeModal(false); refetch() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao vincular bike'),
  })

  const salvarDados = useMutation({
    mutationFn: async () => (await api.put(`/servicos/${id}`, {
      prioridade: formDados.prioridade,
      tecnico_cad_id: formDados.tecnico_cad_id ? Number(formDados.tecnico_cad_id) : null,
      data_prevista: formDados.data_prevista || null,
      forma_pagamento: formDados.forma_pagamento || null,
      descricao_problema: formDados.descricao_problema || null,
      diagnostico: formDados.diagnostico || null,
      laudo_tecnico: formDados.laudo_tecnico || null,
      observacoes_cliente: formDados.observacoes_cliente || null,
      observacoes_internas: formDados.observacoes_internas || null,
    })).data,
    onSuccess: () => { toast.success('OS atualizada'); setEditDados(false); refetch() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const abrirEdicao = () => {
    setFormDados({
      prioridade: os?.prioridade || 'normal',
      tecnico_cad_id: os?.tecnico_cad_id || '',
      data_prevista: os?.data_prevista ? String(os.data_prevista).slice(0, 16) : '',
      forma_pagamento: os?.forma_pagamento || '',
      descricao_problema: os?.descricao_problema || '',
      diagnostico: os?.diagnostico || '',
      laudo_tecnico: os?.laudo_tecnico || '',
      observacoes_cliente: os?.observacoes_cliente || '',
      observacoes_internas: os?.observacoes_internas || '',
    })
    setEditDados(true)
  }

  const adicionarItem = useMutation({
    mutationFn: async () => (await api.post(`/servicos/${id}/itens`, {
      produto_id: novoItem.produto_id,
      descricao: novoItem.descricao || undefined,
      quantidade: novoItem.quantidade,
      preco_unit: novoItem.preco_unit,
      desconto: novoItem.desconto,
      origem_mp: novoItem.origem_mp,
      garantia_dias: novoItem.garantia_dias,
    })).data,
    onSuccess: () => {
      toast.success('Item adicionado')
      setNovoItem(EMPTY_ITEM); setBuscaProd(''); setAbrirBusca(false)
      refetch()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao adicionar item'),
  })

  async function uploadAnexo(file: File, tipo: string) {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('tipo', tipo)
    const token = localStorage.getItem('pain_token')
    const r = await fetch(`/api/servicos/${id}/anexos`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    })
    if (!r.ok) {
      const err = await r.json().catch(() => ({ detail: r.statusText }))
      throw new Error(err.detail || `HTTP ${r.status}`)
    }
    toast.success('Anexo enviado')
    refetch()
  }

  if (isLoading || !os) return <div className="p-6">Carregando...</div>

  const acoes = ACOES[os.status] || []
  // 05/10: mecânico é obrigatório pra INICIAR (não pra criar — no balcão a OS
  // abre antes de se saber quem pega o serviço). O backend recusa; aqui o botão
  // fica travado e esta flag alimenta o aviso que diz ONDE resolver.
  const faltaMecanico = acoes.some((a: any) => a.key === 'iniciar') && !os.tecnico_cad_id
  // "Voltar etapa": disponível em qualquer status ativo, menos os que já geraram
  // documento/efeito (faturada/encerrada/cancelada) e o inicial (rascunho).
  const podeVoltar = !['faturada', 'encerrada', 'cancelada', 'rascunho'].includes(os.status)
  // "Cancelar OS": botão dedicado, visível em todos os status ATIVOS (espelha o
  // backend _acoes_validas, que aceita cancelar em tudo menos faturada/encerrada/
  // reprovada/cancelada). Antes só existia no rascunho — faltava ao abrir a OS.
  const podeCancelar = !['faturada', 'encerrada', 'reprovada', 'cancelada'].includes(os.status)
  // "Excluir" (arquivar): disponível enquanto a OS não está arquivada.
  const podeExcluir = !os.arquivada
  const portalUrl = `${window.location.origin}/portal-os/${os.token_publico}`

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <button className="btn-ghost" onClick={() => nav('/servicos')}>
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Wrench className="w-6 h-6" /> OS #{os.numero}
        </h1>
        <span className={`px-3 py-1 rounded text-sm ${STATUS_COR[os.status] || 'bg-gray-100'}`}>
          {os.status.replace(/_/g, ' ')}
        </span>
        {os.prioridade === 'urgente' && (
          <span className="px-2 py-1 rounded text-xs bg-red-600 text-white font-bold">URGENTE</span>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          <button onClick={() => iaDiag.mutate()} disabled={iaDiag.isPending}
            className="px-3 py-2 rounded flex items-center gap-1.5 text-sm bg-violet-100 hover:bg-violet-200 text-violet-800 border border-violet-300 disabled:opacity-50"
            title="Sofia sugere diagnóstico, laudo e checklist a partir do problema">
            <Sparkles className="w-4 h-4" /> {iaDiag.isPending ? 'Pensando...' : 'Sofia'}
          </button>
          <button onClick={() => setPreviewOpen(true)}
            className="px-3 py-2 rounded flex items-center gap-1.5 text-sm bg-gray-100 hover:bg-gray-200">
            <Printer className="w-4 h-4" /> Visualizar / Imprimir
          </button>
          <button onClick={() => setWaOpen(true)}
            className="px-3 py-2 rounded flex items-center gap-1.5 text-sm bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-emerald-300">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </button>
        </div>
      </div>

      {waOpen && <EnviarWhatsAppModal osId={id!} numero={os.numero} telefone={cliente?.telefone || ''} onClose={() => setWaOpen(false)} />}
      {previewOpen && <PreviewOSModal osId={id!} numero={os.numero} onClose={() => setPreviewOpen(false)} />}
      {showBikeModal && (
        <ModalVincularBike
          clienteId={os.cliente_id}
          atual={os.equipamento_id}
          onClose={() => setShowBikeModal(false)}
          onSelecionar={(id) => vincularBike.mutate(id)}
          salvando={vincularBike.isPending}
        />
      )}

      {/* FEATURE 06/07: modal de Faturamento — separa à vista (PDV→caixa) de a prazo (título). */}
      {showFaturar && (
        <div className="fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4" onClick={() => setShowFaturar(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><DollarSign className="w-5 h-5 text-emerald-700" /></div>
              <div>
                <h3 className="text-lg font-bold text-ink-900">Faturar OS #{os.numero}</h3>
                <p className="text-xs text-ink-500">Total: <strong>{formatMoney(Number(os.valor_total))}</strong></p>
              </div>
            </div>

            {/* À VISTA → PDV (entra no caixa) */}
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
              <div className="text-xs font-semibold text-emerald-800 uppercase mb-1">
                À vista — dinheiro, PIX, cartão
              </div>
              <p className="text-[11px] text-ink-500 mb-2">
                O pagamento é feito no PDV para o valor entrar no caixa do dia. Registrar
                aqui geraria um título a receber, e o dinheiro não apareceria no fechamento.
              </p>
              <button
                className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center justify-center gap-2"
                onClick={() => nav('/pdv', { state: { balcaoOS: os.numero } })}>
                <Monitor className="w-4 h-4" /> Cobrar no PDV (dinheiro, PIX, cartão)
              </button>
            </div>

            {/* A PRAZO → título a receber */}
            <div className="mt-3 rounded-xl border border-ink-200 p-3">
              <div className="text-xs font-semibold text-ink-700 uppercase mb-2">A prazo (a receber)</div>
              <div className="flex gap-2">
                <select className="input flex-1" value={fatForma} onChange={e => setFatForma(e.target.value)}>
                  <option value="boleto">Boleto</option>
                  <option value="crediario">Crediário</option>
                  <option value="prazo">A prazo (faturado)</option>
                </select>
                <input type="date" className="input w-40" value={fatVenc} onChange={e => setFatVenc(e.target.value)} title="Vencimento (opcional)" />
              </div>
              <button
                className="mt-2 w-full py-2.5 rounded-lg bg-ink-700 hover:bg-ink-800 text-white font-semibold disabled:opacity-60"
                disabled={faturarAPrazo.isPending}
                onClick={() => faturarAPrazo.mutate()}>
                {faturarAPrazo.isPending ? 'Gerando...' : 'Gerar título a receber'}
              </button>
              <p className="text-[11px] text-ink-400 mt-1">Vai pro Contas a Receber — entra no caixa só quando o título for recebido.</p>
            </div>

            <button className="mt-4 w-full text-sm text-ink-500 hover:text-ink-700" onClick={() => setShowFaturar(false)}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Modal sugestão da Sofia */}
      {iaSugestao && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setIaSugestao(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 p-3">
              <h2 className="font-bold text-sm flex items-center gap-2 text-violet-800"><Sparkles className="w-4 h-4" /> Sugestão da Sofia</h2>
              <button onClick={() => setIaSugestao(null)} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <p className="text-xs text-gray-500">Revise antes de aplicar — a Sofia sugere, você decide.</p>
              {iaSugestao.diagnostico && (
                <div><div className="text-xs font-semibold text-violet-700 mb-0.5">DIAGNÓSTICO PROVÁVEL</div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap">{iaSugestao.diagnostico}</div></div>
              )}
              {iaSugestao.laudo_tecnico && (
                <div><div className="text-xs font-semibold text-violet-700 mb-0.5">LAUDO TÉCNICO</div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap">{iaSugestao.laudo_tecnico}</div></div>
              )}
              {iaSugestao.checklist?.length > 0 && (
                <div><div className="text-xs font-semibold text-violet-700 mb-0.5">CHECKLIST</div>
                  <ul className="list-disc list-inside bg-gray-50 border border-gray-200 rounded p-2 space-y-0.5">
                    {iaSugestao.checklist.map((t: string, i: number) => <li key={i}>{t}</li>)}
                  </ul></div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 p-3">
              <button onClick={() => setIaSugestao(null)} className="text-xs px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50">Descartar</button>
              <button onClick={() => aplicarIa.mutate()} disabled={aplicarIa.isPending}
                className="text-xs px-3 py-1.5 rounded bg-violet-600 hover:bg-violet-700 text-white">
                {aplicarIa.isPending ? 'Aplicando...' : 'Aplicar na OS'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FEATURE 06/07: banner de OS de garantia/retorno (destaque visual persistente). */}
      {os.eh_garantia && (
        <div className="os-garantia-banner mb-4 flex items-center gap-2 rounded-xl border px-4 py-2.5">
          <Shield className="w-5 h-5 shrink-0" />
          <span className="font-semibold text-sm">OS de Garantia / Retorno</span>
          <span className="text-xs opacity-80">— sem cobrança (retrabalho coberto)</span>
          {os.os_original_id && (
            <button onClick={() => nav(`/servicos/${os.os_original_id}`)}
              className="ml-auto text-xs font-medium underline hover:opacity-80">
              ver OS original
            </button>
          )}
        </div>
      )}

      {/* Header — cliente + valor + garantia */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="card p-3">
          <div className="text-xs text-gray-500 uppercase">Cliente</div>
          <div className="font-medium">{cliente?.nome || `#${os.cliente_id}`}</div>
          <div className="text-xs text-gray-500">{cliente?.telefone || cliente?.documento}</div>
        </div>
        <div className="card p-3">
          <div className="text-xs text-gray-500 uppercase">Valor Total</div>
          <div className="text-xl font-bold">{formatMoney(Number(os.valor_total))}</div>
          <div className="text-xs text-gray-500">
            Serviços: {formatMoney(Number(os.valor_servicos))}
          </div>
        </div>
        <div className="card p-3">
          <div className="text-xs text-gray-500 uppercase">Abertura</div>
          <div>{formatDateTime(os.data_abertura)}</div>
          {os.data_prevista && (
            <div className="text-xs text-gray-500">Prev: {formatDateTime(os.data_prevista)}</div>
          )}
        </div>
        <div className="card p-3">
          <div className="text-xs text-gray-500 uppercase">Garantia</div>
          {/* FEATURE 06/07: card antes ficava "—" e confundia. Agora explica o estado:
              data + vigente/vencida (se já concluída) · X dias a partir da conclusão
              (se ainda não concluiu mas tem prazo nos itens) · "Sem garantia". */}
          {(() => {
            const maxDias = os.itens.length ? Math.max(0, ...os.itens.map((i: any) => Number(i.garantia_dias) || 0)) : 0
            if (os.garantia_ate) {
              const fim = new Date(os.garantia_ate + 'T00:00:00')
              const hoje = new Date(); hoje.setHours(0, 0, 0, 0)
              const dias = Math.ceil((fim.getTime() - hoje.getTime()) / 86400000)
              const vig = dias >= 0
              return (
                <>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium">{fim.toLocaleDateString('pt-BR')}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${vig ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'}`}>
                      {vig ? `faltam ${dias}d` : 'VENCIDA'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">{os.garantia_dias} dias de garantia</div>
                </>
              )
            }
            if (maxDias > 0) {
              return <div className="text-sm text-gray-600">{maxDias} dias <span className="text-xs text-gray-400">(a partir da conclusão)</span></div>
            }
            return <div className="text-gray-400 text-sm">Sem garantia</div>
          })()}
        </div>
      </div>

      {/* Ações de transição */}
      {(acoes.length > 0 || podeVoltar || podeCancelar || podeExcluir) && (
        <div className="card p-3 mb-4 flex flex-wrap gap-2">
          {/* w-full pra ocupar a linha inteira: o container é flex-wrap, senão o
              aviso disputaria espaço com os botões. */}
          {faltaMecanico && (
            <div className="w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <strong>Sem mecânico definido.</strong> Escolha o responsável na aba{' '}
              <button type="button" className="underline font-medium"
                onClick={() => { setTab('dados'); abrirEdicao() }}>
                Dados
              </button>{' '}
              para liberar o início da execução.
            </div>
          )}
          {podeVoltar && (
            <button
              className="px-3 py-2 rounded flex items-center gap-2 text-sm bg-amber-50 text-amber-800 hover:bg-amber-100"
              onClick={() => {
                const motivo = prompt('Motivo para voltar a OS ao passo anterior:')
                if (!motivo || !motivo.trim()) return
                transicao.mutate({ acao: 'reverter', observacao: motivo.trim() })
              }}
              disabled={transicao.isPending}
              title="Volta a OS para o status anterior (fica registrado na Timeline)"
            >
              <RotateCcw className="w-4 h-4" />
              Voltar etapa
            </button>
          )}
          {os.status === 'faturada' && (
            <button
              className="px-3 py-2 rounded flex items-center gap-2 text-sm bg-amber-50 text-amber-800 hover:bg-amber-100"
              onClick={() => {
                const motivo = prompt('Motivo do estorno do faturamento:')
                if (!motivo || !motivo.trim()) return
                transicao.mutate({ acao: 'estornar-faturamento', observacao: motivo.trim() })
              }}
              disabled={transicao.isPending}
              title="Desfaz o faturamento e volta a OS ao passo anterior"
            >
              <RotateCcw className="w-4 h-4" />
              Estornar faturamento
            </button>
          )}
          {acoes.map(a => {
            const Icon = a.icon
            return (
              <button
                key={a.key}
                className={`px-3 py-2 rounded flex items-center gap-2 text-sm ${a.cor || 'bg-gray-100 hover:bg-gray-200'}`}
                onClick={() => {
                  if (a.key === 'cancelar') {
                    const motivo = prompt('Motivo do cancelamento da OS:')
                    if (!motivo || !motivo.trim()) return
                    transicao.mutate({ acao: 'cancelar', observacao: motivo.trim() })
                    return
                  }
                  if (a.key === 'garantia') {
                    const desc = prompt('Descreva o problema reportado em garantia:')
                    if (!desc) return
                    api.post(`/servicos/${id}/garantia`, { descricao_problema: desc })
                      .then(r => { toast.success(`OS #${r.data.numero} aberta em garantia`); nav(`/servicos/${r.data.id}`) })
                      .catch(e => toast.error(e?.response?.data?.detail || 'Erro'))
                    return
                  }
                  if (a.key === 'faturar') {
                    // Garantia tem preços zerados: não há o que cobrar. Pula o modal
                    // de pagamento e encerra direto. Sem isto a OS fica PRESA em
                    // 'entregue' — 'encerrar' só existe a partir de 'faturada'.
                    if (os.eh_garantia && Number(os.valor_total || 0) <= 0) {
                      if (window.confirm(`Encerrar a OS #${os.numero} sem cobrança (garantia)?`)) {
                        transicao.mutate('faturar')
                      }
                      return
                    }
                    setShowFaturar(true); return
                  }
                  transicao.mutate(a.key)
                }}
                disabled={transicao.isPending
                  || (a.key === 'iniciar' && !os.tecnico_cad_id)}
                title={a.key === 'iniciar' && !os.tecnico_cad_id
                  ? 'Defina o mecânico responsável na aba Dados antes de iniciar'
                  : undefined}
              >
                <Icon className="w-4 h-4" />
                {a.key === 'faturar' && os.eh_garantia && Number(os.valor_total || 0) <= 0
                  ? 'Encerrar sem cobrança'
                  : a.label}
              </button>
            )
          })}

          {/* Ações destrutivas separadas à direita: Cancelar (status ativo) e Excluir (arquivar). */}
          {(podeCancelar || podeExcluir) && (
            <div className="ml-auto flex flex-wrap gap-2">
              {podeCancelar && (
                <button
                  className="px-3 py-2 rounded flex items-center gap-2 text-sm bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                  onClick={() => {
                    const motivo = prompt('Motivo do cancelamento da OS:')
                    if (!motivo || !motivo.trim()) return
                    transicao.mutate({ acao: 'cancelar', observacao: motivo.trim() })
                  }}
                  disabled={transicao.isPending}
                  title="Cancela a OS (motivo obrigatório, registrado na Timeline; estorna estoque dos itens)"
                >
                  <XCircle className="w-4 h-4" />
                  Cancelar OS
                </button>
              )}
              {podeExcluir && (
                <button
                  className="px-3 py-2 rounded flex items-center gap-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                  onClick={() => pedirExclusao({ descricao: `a OS #${os.numero}`, confirmLabel: 'Excluir', onConfirm: () => arquivar.mutate() })}
                  disabled={arquivar.isPending}
                  title="Arquiva a OS (soft-delete): some das listas mas pode ser restaurada"
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* NFS-e — Nota de Serviço (opcional, após faturar) */}
      {podeNfse && (
        <div className="card p-3 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-violet-600" />
              <span className="font-semibold text-sm">Nota Fiscal de Serviço (NFS-e)</span>
              {nfse?.status === 'autorizado' && (
                <span className="px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-800">
                  Autorizada {nfse.numero ? `· nº ${nfse.numero}` : ''}
                </span>
              )}
              {nfse?.status === 'processando' && (
                <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">Processando</span>
              )}
              {nfse?.status === 'rejeitado' && (
                <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Rejeitada</span>
              )}
              {nfse?.status === 'cancelado' && (
                <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Cancelada</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {(!nfse || nfse.status === 'cancelado' || nfse.status === 'rejeitado') && (
                <button onClick={() => emitirNfse.mutate()} disabled={emitirNfse.isPending}
                  className="px-3 py-1.5 rounded text-sm bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-1.5 disabled:opacity-50">
                  <FileText className="w-4 h-4" /> {emitirNfse.isPending ? 'Emitindo...' : 'Emitir NFS-e'}
                </button>
              )}
              {nfse && (nfse.status === 'autorizado' || nfse.status === 'cancelado') && (
                <button onClick={abrirNfsePdf}
                  className="px-3 py-1.5 rounded text-sm bg-gray-100 hover:bg-gray-200 flex items-center gap-1.5">
                  <Printer className="w-4 h-4" /> Imprimir NFS-e
                </button>
              )}
              {nfse?.status === 'autorizado' && (
                <button onClick={() => {
                  const j = prompt('Justificativa do cancelamento (mín. 15 caracteres):')
                  if (j && j.length >= 15) cancelarNfse.mutate(j)
                  else if (j !== null) toast.error('Justificativa muito curta')
                }} disabled={cancelarNfse.isPending}
                  className="px-3 py-1.5 rounded text-sm bg-red-50 text-red-700 hover:bg-red-100 flex items-center gap-1.5 disabled:opacity-50">
                  <XCircle className="w-4 h-4" /> Cancelar NFS-e
                </button>
              )}
            </div>
          </div>
          {nfse?.status === 'rejeitado' && nfse?.mensagem && (
            <p className="text-xs text-red-600 mt-2">{nfse.mensagem}</p>
          )}
          {!nfse && (
            <p className="text-xs text-gray-500 mt-2">Opcional — emita a nota de serviço se o cliente precisar.</p>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b mb-3 flex gap-1">
        {(['dados', 'itens', 'timeline', 'anexos', 'portal'] as const).map(t => (
          <button
            key={t}
            className={`px-4 py-2 text-sm capitalize ${tab === t ? 'border-b-2 border-indigo-600 font-semibold' : 'text-gray-500'}`}
            onClick={() => setTab(t)}
          >
            {t === 'dados' && 'Dados'}
            {t === 'itens' && `Itens (${os.itens.length})`}
            {t === 'timeline' && `Timeline (${os.eventos.length})`}
            {t === 'anexos' && `Anexos (${os.anexos.length})`}
            {t === 'portal' && 'Portal Cliente'}
          </button>
        ))}
      </div>

      {tab === 'dados' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {!['faturada', 'encerrada', 'reprovada', 'cancelada'].includes(os.status) && (
            <div className="md:col-span-2 flex justify-end">
              {!editDados ? (
                <button className="btn-ghost flex items-center gap-1.5" onClick={abrirEdicao}>
                  <Pencil className="w-4 h-4" /> Editar dados
                </button>
              ) : (
                <div className="flex gap-2">
                  <button className="btn-ghost" onClick={() => setEditDados(false)}>Cancelar</button>
                  <button className="btn-primary flex items-center gap-1.5" disabled={salvarDados.isPending}
                    onClick={() => salvarDados.mutate()}>
                    {salvarDados.isPending ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                </div>
              )}
            </div>
          )}
          {editDados ? (
            <div className="md:col-span-2 card p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-gray-500 uppercase">Prioridade</label>
                  <select className="input" value={formDados.prioridade}
                    onChange={e => setFormDados({ ...formDados, prioridade: e.target.value })}>
                    <option value="baixa">Baixa</option>
                    <option value="normal">Normal</option>
                    <option value="alta">Alta</option>
                    <option value="urgente">URGENTE</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">
                    Mecânico / Técnico <span className="text-rose-500">*</span>
                  </label>
                  <select className={`input ${!formDados.tecnico_cad_id ? 'ring-1 ring-amber-400' : ''}`}
                    value={formDados.tecnico_cad_id}
                    onChange={e => setFormDados({ ...formDados, tecnico_cad_id: e.target.value })}>
                    <option value="">—</option>
                    {tecnicos.map((t: any) => (<option key={t.id} value={t.id}>{t.nome}</option>))}
                  </select>
                  {!formDados.tecnico_cad_id && (
                    <p className="text-[11px] text-amber-600 mt-0.5">
                      Obrigatório para iniciar a execução.
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Data prevista</label>
                  <input type="datetime-local" className="input" value={formDados.data_prevista}
                    onChange={e => setFormDados({ ...formDados, data_prevista: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Forma de pagamento</label>
                  <select className="input" value={formDados.forma_pagamento}
                    onChange={e => setFormDados({ ...formDados, forma_pagamento: e.target.value })}>
                    <option value="">—</option>
                    <option value="dinheiro">Dinheiro</option>
                    <option value="pix">PIX</option>
                    <option value="cartao_credito">Cartão de crédito</option>
                    <option value="cartao_debito">Cartão de débito</option>
                    <option value="boleto">Boleto</option>
                    <option value="faturado">Faturado</option>
                    <option value="crediario">Crediário</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Problema relatado</label>
                <textarea className="input min-h-[60px]" value={formDados.descricao_problema}
                  onChange={e => setFormDados({ ...formDados, descricao_problema: e.target.value })} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 uppercase">Diagnóstico</label>
                  <textarea className="input min-h-[60px]" value={formDados.diagnostico}
                    onChange={e => setFormDados({ ...formDados, diagnostico: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Laudo técnico</label>
                  <textarea className="input min-h-[60px]" value={formDados.laudo_tecnico}
                    onChange={e => setFormDados({ ...formDados, laudo_tecnico: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Observações ao cliente</label>
                  <textarea className="input min-h-[60px]" value={formDados.observacoes_cliente}
                    onChange={e => setFormDados({ ...formDados, observacoes_cliente: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Observações internas</label>
                  <textarea className="input min-h-[60px]" value={formDados.observacoes_internas}
                    onChange={e => setFormDados({ ...formDados, observacoes_internas: e.target.value })} />
                </div>
              </div>
            </div>
          ) : (<>
          <div className="card p-3">
            <div className="text-xs text-gray-500 uppercase mb-1">Problema relatado</div>
            <div className="whitespace-pre-wrap">{os.descricao_problema || '—'}</div>
          </div>
          <div className="card p-3">
            <div className="text-xs text-gray-500 uppercase mb-1">Diagnóstico</div>
            <div className="whitespace-pre-wrap">{os.diagnostico || '—'}</div>
          </div>
          <div className="card p-3">
            <div className="text-xs text-gray-500 uppercase mb-1">Laudo técnico</div>
            <div className="whitespace-pre-wrap">{os.laudo_tecnico || '—'}</div>
          </div>
          <div className="card p-3">
            <div className="text-xs text-gray-500 uppercase mb-1">Observações cliente</div>
            <div className="whitespace-pre-wrap">{os.observacoes_cliente || '—'}</div>
          </div>
          </>)}
          <div className="card p-3 md:col-span-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Bike className="w-4 h-4 text-violet-600 shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-gray-500 uppercase">Bike vinculada</div>
                <div className="font-medium truncate">
                  {os.equipamento_nome || <span className="text-gray-400 font-normal">Nenhuma bike vinculada</span>}
                </div>
              </div>
            </div>
            {osAberta && (
              <button className="btn-ghost flex items-center gap-1.5 shrink-0" onClick={() => setShowBikeModal(true)}>
                <Pencil className="w-3.5 h-3.5" /> {os.equipamento_nome ? 'Trocar' : 'Vincular bike'}
              </button>
            )}
          </div>
          {os.campos_custom && Object.keys(os.campos_custom).length > 0 && (
            <div className="card p-3 md:col-span-2">
              <div className="text-xs text-gray-500 uppercase mb-2">Campos do segmento</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(os.campos_custom).map(([k, v]) => (
                  <div key={k}>
                    <div className="text-xs text-gray-500">{k}</div>
                    <div className="font-mono">{String(v)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {os.checklist && Array.isArray(os.checklist) && os.checklist.length > 0 && (
            <div className="card p-3 md:col-span-2">
              <div className="text-xs text-gray-500 uppercase mb-2 flex items-center gap-1">
                <ListChecks className="w-3 h-3" /> Checklist
              </div>
              <ul className="space-y-1 text-sm">
                {os.checklist.map((c: any, i: number) => (
                  <li key={i} className="flex items-center gap-2">
                    <input type="checkbox" checked={c.ok || false} readOnly />
                    <span>{c.item || c.label || JSON.stringify(c)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === 'itens' && (
        <div className="space-y-3">
        {!['faturada', 'encerrada', 'reprovada', 'cancelada'].includes(os.status) && (
          <div className="card p-4">
            <div className="text-xs text-gray-500 uppercase mb-2 flex items-center gap-1.5"><Plus size={13} /> Adicionar item (serviço / peça)</div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
              <div className="md:col-span-2 relative">
                <label className="text-xs text-gray-500">Produto/Serviço</label>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    className="input pl-9"
                    placeholder="Digite código ou nome..."
                    value={buscaProd}
                    autoComplete="off"
                    onChange={e => { setBuscaProd(e.target.value); setAbrirBusca(true); if (novoItem.produto_id) setNovoItem({ ...novoItem, produto_id: null }) }}
                    onFocus={() => setAbrirBusca(true)}
                  />
                </div>
                {abrirBusca && buscaProd.trim() && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-20 max-h-80 overflow-y-auto">
                    {produtosBusca.length === 0 ? (
                      <div className="p-3 text-center text-sm text-gray-500">Nenhum produto encontrado</div>
                    ) : (
                      (['servico', 'produto'] as const).map(grupo => {
                        const itens = produtosBusca.filter((p: any) => p.tipo === grupo)
                        if (itens.length === 0) return null
                        return (
                          <div key={grupo}>
                            <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-400 bg-gray-50 sticky top-0">
                              {grupo === 'servico' ? '🔧 Serviços' : '📦 Produtos / Peças'}
                            </div>
                            {itens.map((p: any) => (
                              <button key={p.id} type="button" onClick={() => {
                                setNovoItem({ ...novoItem, produto_id: p.id, descricao: p.descricao || '', preco_unit: Number(p.preco_venda || 0) })
                                setBuscaProd(`${p.codigo} — ${p.descricao}`)
                                setAbrirBusca(false)
                              }}
                                className="w-full px-3 py-2 text-left border-b last:border-0 hover:bg-gray-50 flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate" title={p.descricao}>{p.descricao}</p>
                                  <span className="text-xs text-gray-500 font-mono">{p.codigo}</span>
                                </div>
                                <span className="text-sm font-bold text-emerald-700 flex-shrink-0">{formatMoney(Number(p.preco_venda || 0))}</span>
                              </button>
                            ))}
                          </div>
                        )
                      })
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500">Qtd</label>
                <input type="number" step="0.001" min="0" className="input text-right"
                  value={novoItem.quantidade}
                  onChange={e => setNovoItem({ ...novoItem, quantidade: Number(e.target.value) })} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Preço</label>
                <input type="number" step="0.01" min="0" className="input text-right"
                  value={novoItem.preco_unit}
                  onChange={e => setNovoItem({ ...novoItem, preco_unit: Number(e.target.value) })} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Origem MP</label>
                <select className="input"
                  value={novoItem.origem_mp}
                  onChange={e => setNovoItem({ ...novoItem, origem_mp: e.target.value })}>
                  <option value="estoque_proprio">Estoque</option>
                  <option value="cliente_traz">Cliente traz</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Garantia (dias)</label>
                <input type="number" min="0" className="input text-right"
                  value={novoItem.garantia_dias}
                  onChange={e => setNovoItem({ ...novoItem, garantia_dias: Number(e.target.value) })} />
              </div>
            </div>
            <button
              type="button"
              className="btn-primary mt-3 flex items-center gap-1 disabled:opacity-50"
              disabled={!novoItem.produto_id || adicionarItem.isPending}
              onClick={() => { if (!novoItem.produto_id) { toast.error('Selecione um produto'); return } adicionarItem.mutate() }}
            >
              <Plus className="w-4 h-4" /> {adicionarItem.isPending ? 'Adicionando...' : 'Adicionar item'}
            </button>
          </div>
        )}
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 border-b">
              <tr>
                <th className="text-left py-2 px-3">Tipo</th>
                <th className="text-left py-2 px-3">Descrição</th>
                <th className="text-right py-2 px-3">Qtd</th>
                <th className="text-right py-2 px-3">Preço</th>
                <th className="text-right py-2 px-3">Desconto</th>
                <th className="text-right py-2 px-3">Subtotal</th>
                <th className="text-left py-2 px-3">Origem MP</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">OP</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {os.itens.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-gray-400 text-sm">Nenhum item lançado ainda.</td></tr>
              )}
              {os.itens.map((it: any) => {
                const emEdicao = editItemId === it.id
                const st = STATUS_ITEM_META[it.status_item] || { label: it.status_item, cor: 'bg-gray-100 text-gray-700' }
                if (emEdicao) {
                  const subPrev = Math.max(0, (Number(editItem.preco_unit) || 0) * (Number(editItem.quantidade) || 0) - (Number(editItem.desconto) || 0))
                  return (
                    <tr key={it.id} className="border-b bg-violet-50/60">
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                          it.eh_servico ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {it.eh_servico ? '🔧 Serviço' : '📦 Peça'}
                        </span>
                      </td>
                      <td className="py-2 px-3 relative">
                        <div className="relative">
                          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            className="input py-1 pl-7 text-sm w-full"
                            placeholder="Digite código ou nome..."
                            value={editBuscaProd}
                            autoComplete="off"
                            onChange={e => { setEditBuscaProd(e.target.value); setEditAbrirBusca(true) }}
                            onFocus={() => setEditAbrirBusca(true)}
                          />
                        </div>
                        {editAbrirBusca && editBuscaProd.trim() && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-20 max-h-72 overflow-y-auto">
                            {produtosBuscaEdit.length === 0 ? (
                              <div className="p-3 text-center text-sm text-gray-500">Nenhum produto encontrado</div>
                            ) : (
                              (['servico', 'produto'] as const).map(grupo => {
                                const itens = produtosBuscaEdit.filter((p: any) => p.tipo === grupo)
                                if (itens.length === 0) return null
                                return (
                                  <div key={grupo}>
                                    <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-400 bg-gray-50 sticky top-0">
                                      {grupo === 'servico' ? '🔧 Serviços' : '📦 Produtos / Peças'}
                                    </div>
                                    {itens.map((p: any) => (
                                      <button key={p.id} type="button" onClick={() => {
                                        setEditItem({ ...editItem, produto_id: p.id, descricao: p.descricao || '', preco_unit: Number(p.preco_venda || 0) })
                                        setEditBuscaProd(`${p.codigo} — ${p.descricao}`)
                                        setEditAbrirBusca(false)
                                      }}
                                        className="w-full px-3 py-2 text-left border-b last:border-0 hover:bg-gray-50 flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                          <p className="text-sm font-medium truncate" title={p.descricao}>{p.descricao}</p>
                                          <span className="text-xs text-gray-500 font-mono">{p.codigo}</span>
                                        </div>
                                        <span className="text-sm font-bold text-emerald-700 flex-shrink-0">{formatMoney(Number(p.preco_venda || 0))}</span>
                                      </button>
                                    ))}
                                  </div>
                                )
                              })
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <input type="number" step="0.001" min="0" className="input py-1 text-sm text-right w-20"
                          value={editItem.quantidade}
                          onChange={e => setEditItem({ ...editItem, quantidade: e.target.value })} />
                      </td>
                      <td className="py-2 px-3">
                        <input type="number" step="0.01" min="0" className="input py-1 text-sm text-right w-24"
                          value={editItem.preco_unit}
                          onChange={e => setEditItem({ ...editItem, preco_unit: e.target.value })} />
                      </td>
                      <td className="py-2 px-3">
                        <input type="number" step="0.01" min="0" className="input py-1 text-sm text-right w-24"
                          value={editItem.desconto}
                          onChange={e => setEditItem({ ...editItem, desconto: e.target.value })} />
                      </td>
                      <td className="py-2 px-3 text-right font-mono font-semibold text-violet-700">{formatMoney(subPrev)}</td>
                      <td className="py-2 px-3">
                        <select className="input py-1 text-sm" value={editItem.origem_mp} disabled={it.eh_servico}
                          onChange={e => setEditItem({ ...editItem, origem_mp: e.target.value })}>
                          <option value="estoque_proprio">📦 Estoque</option>
                          <option value="cliente_traz">🧳 Cliente</option>
                        </select>
                      </td>
                      <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded text-xs ${st.cor}`}>{st.label}</span></td>
                      <td className="py-2 px-3 text-gray-300">—</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        <button className="text-emerald-600 hover:text-emerald-800 disabled:opacity-50" title="Salvar"
                          disabled={atualizarItem.isPending}
                          onClick={() => atualizarItem.mutate()}>
                          <CheckCircle2 className="w-4 h-4 inline" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-700 ml-2" title="Cancelar"
                          onClick={() => setEditItemId(null)}>
                          <X className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  )
                }
                return (
                  <tr key={it.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                        it.eh_servico ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {it.eh_servico ? '🔧 Serviço' : '📦 Peça'}
                      </span>
                    </td>
                    <td className="py-2 px-3" title={it.descricao}>
                      {it.descricao}
                      {it.garantia_dias > 0 && (() => {
                        const gi = garantiaItem(it)
                        if (!gi) return <div className="text-[10px] text-gray-400 mt-0.5">🛡 garantia {it.garantia_dias}d (conta após concluir)</div>
                        return (
                          <div className={`text-[10px] mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${gi.vigente ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'}`}>
                            <Shield className="w-2.5 h-2.5" />
                            {gi.vigente ? `garantia até ${gi.ate} · faltam ${gi.dias}d` : `garantia vencida em ${gi.ate}`}
                          </div>
                        )
                      })()}
                    </td>
                    <td className="py-2 px-3 text-right font-mono">{it.quantidade}</td>
                    <td className="py-2 px-3 text-right font-mono">{formatMoney(Number(it.preco_unit))}</td>
                    <td className="py-2 px-3 text-right font-mono text-rose-600">{Number(it.desconto) > 0 ? '− ' + formatMoney(Number(it.desconto)) : '—'}</td>
                    <td className="py-2 px-3 text-right font-mono font-semibold">{formatMoney(Number(it.subtotal))}</td>
                    <td className="py-2 px-3 text-xs">
                      {it.eh_servico ? <span className="text-gray-300">—</span> : (it.origem_mp === 'cliente_traz' ? '🧳 Cliente' : '📦 Estoque')}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${st.cor}`}>{st.label}</span>
                    </td>
                    <td className="py-2 px-3">
                      {it.op_gerada_id ? (
                        <Link to={`/industrial/ordens/${it.op_gerada_id}`} className="text-indigo-600 text-xs underline flex items-center gap-1">
                          OP #{it.op_gerada_id} <ExternalLink className="w-3 h-3" />
                        </Link>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      {itemEditavel(it) && (
                        <button className="text-gray-400 hover:text-violet-600" title="Editar item"
                          onClick={() => iniciarEdicaoItem(it)}>
                          <Pencil className="w-3.5 h-3.5 inline" />
                        </button>
                      )}
                      {it.status_item === 'pendente' && (
                        <>
                          <button className="text-emerald-600 hover:text-emerald-800 ml-2" title="Aprovar item" onClick={() => aprovarItem.mutate(it.id)}>
                            <CheckCircle2 className="w-3.5 h-3.5 inline" />
                          </button>
                          <button className="text-red-600 hover:text-red-800 ml-2" title="Reprovar item" onClick={() => reprovarItem.mutate(it.id)}>
                            <XCircle className="w-3.5 h-3.5 inline" />
                          </button>
                        </>
                      )}
                      {['pendente', 'reprovado'].includes(it.status_item) && !it.baixou_estoque && !it.op_gerada_id && (
                        <button
                          className="text-gray-400 hover:text-red-600 ml-2" title="Remover item"
                          onClick={() => pedirExclusao({ descricao: 'o item', onConfirm: () => removerItem.mutate(it.id) })}
                        >
                          <Trash2 className="w-3.5 h-3.5 inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
            {os.itens.length > 0 && (
              <tfoot className="border-t-2 border-gray-200 text-sm">
                <tr>
                  <td colSpan={4} className="py-2 px-3 text-right text-gray-500">Serviços</td>
                  <td className="py-2 px-3 text-right font-mono">{formatMoney(Number(os.valor_servicos))}</td>
                  <td colSpan={4}></td>
                </tr>
                <tr>
                  <td colSpan={4} className="py-1 px-3 text-right text-gray-500">Peças</td>
                  <td className="py-1 px-3 text-right font-mono">{formatMoney(Number(os.valor_pecas))}</td>
                  <td colSpan={4}></td>
                </tr>
                {Number(os.valor_desconto) > 0 && (
                  <tr>
                    <td colSpan={4} className="py-1 px-3 text-right text-gray-500">Desconto da OS</td>
                    <td className="py-1 px-3 text-right font-mono text-rose-600">− {formatMoney(Number(os.valor_desconto))}</td>
                    <td colSpan={4}></td>
                  </tr>
                )}
                <tr className="border-t">
                  <td colSpan={4} className="py-2 px-3 text-right font-semibold">Total</td>
                  <td className="py-2 px-3 text-right font-mono font-bold text-base text-emerald-700">{formatMoney(Number(os.valor_total))}</td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        </div>
      )}

      {tab === 'timeline' && (() => {
        // Resumo em 3 marcos: Aberta → Serviço concluído → Finalizada (faturada no
        // PDV, valor no caixa do dia). "Atingido" vem do STATUS atual (respeita o
        // "voltar etapa"); a data/quem vem do evento correspondente. Histórico
        // detalhado completo fica atrás do toggle (nada se perde).
        const ult = (tipo: string) => [...os.eventos].reverse().find((e: any) => e.tipo === tipo)
        const st = os.status
        const cancelada = st === 'cancelada'
        const evCanc = ult('cancelamento')
        const marcos = [
          { ok: true, label: 'Aberta', sub: 'OS criada', ev: ult('abertura') },
          { ok: ['concluida', 'entregue', 'faturada', 'encerrada'].includes(st), label: 'Serviço concluído', sub: 'serviço finalizado', ev: ult('conclusao') },
          { ok: ['faturada', 'encerrada'].includes(st), label: 'Finalizada', sub: 'faturada no PDV · valor no caixa do dia', ev: ult('faturamento') },
        ]
        return (
          <div>
            {!histCompleto ? (
              <div>
                {cancelada && (
                  <div className="card p-3 mb-3 border-l-4 border-red-400 bg-red-50">
                    <div className="font-medium text-red-700">OS Cancelada</div>
                    <div className="text-xs text-gray-600">{evCanc && formatDateTime(evCanc.criado_em)}{evCanc?.usuario_nome ? ` · ${evCanc.usuario_nome}` : ''}</div>
                    {evCanc?.descricao && <div className="text-sm mt-1">{evCanc.descricao}</div>}
                  </div>
                )}
                {marcos.map((m, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${m.ok ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                        {m.ok ? '✓' : i + 1}
                      </div>
                      {i < marcos.length - 1 && <div className={`w-0.5 flex-1 min-h-[26px] ${m.ok ? 'bg-emerald-300' : 'bg-gray-200'}`} />}
                    </div>
                    <div className="pb-4">
                      <div className={`font-medium ${m.ok ? '' : 'text-gray-400'}`}>{m.label}</div>
                      <div className="text-xs text-gray-500">{m.sub}</div>
                      {m.ok && m.ev && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {formatDateTime(m.ev.criado_em)}{m.ev.usuario_nome ? ` · ${m.ev.usuario_nome}` : ''}
                        </div>
                      )}
                      {!m.ok && <div className="text-xs text-gray-400 mt-0.5">pendente</div>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {os.eventos.map((ev: any) => (
                  <div key={ev.id} className="card p-3 flex gap-3 border-l-4 border-indigo-400">
                    <div className="text-xs text-gray-500 w-40 shrink-0">
                      <div>{formatDateTime(ev.criado_em)}</div>
                      {(ev.usuario_nome || ev.usuario_id) && <div className="flex items-center gap-1"><UserIcon className="w-3 h-3" /> {ev.usuario_nome || `#${ev.usuario_id}`}</div>}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{EVENTO_LABEL[ev.tipo] || ev.tipo}</div>
                      {ev.status_anterior && ev.status_novo && (
                        <div className="text-xs text-gray-500">
                          {ev.status_anterior} → <strong>{ev.status_novo}</strong>
                        </div>
                      )}
                      {ev.descricao && <div className="text-sm mt-1">{ev.descricao}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button className="text-xs text-indigo-600 hover:underline mt-2" onClick={() => setHistCompleto(v => !v)}>
              {histCompleto ? '← Ver resumo' : `Ver histórico completo (${os.eventos.length})`}
            </button>
          </div>
        )
      })()}

      {tab === 'anexos' && (
        <div>
          <label className="card p-6 block text-center cursor-pointer border-2 border-dashed mb-4">
            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
            <div className="text-sm text-gray-600">Clique para enviar foto/PDF/XML</div>
            <input
              type="file"
              className="hidden"
              accept="image/*,application/pdf,application/xml"
              onChange={e => {
                const f = e.target.files?.[0]
                if (f) uploadAnexo(f, 'foto').catch((err: any) => toast.error(err.message))
                e.target.value = ''
              }}
            />
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {os.anexos.map((an: any) => (
              <div key={an.id} className="card p-2">
                {an.mime_type?.startsWith('image/') ? (
                  <a href={an.url} target="_blank" rel="noopener noreferrer">
                    <img src={an.url} alt="" className="w-full h-32 object-cover rounded" />
                  </a>
                ) : (
                  <a href={an.url} target="_blank" rel="noopener noreferrer" className="block h-32 flex items-center justify-center bg-gray-50 rounded">
                    <Paperclip className="w-10 h-10 text-gray-400" />
                  </a>
                )}
                <div className="flex items-center justify-between mt-1 text-xs">
                  <span className="truncate">{an.nome_original || an.tipo}</span>
                  <button
                    onClick={() => pedirExclusao({ descricao: 'o anexo', onConfirm: () => removerAnexo.mutate(an.id) })}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'portal' && (
        <div className="card p-4">
          <div className="text-xs text-gray-500 uppercase mb-2">Link público pro cliente acompanhar</div>
          <div className="flex gap-2">
            <input className="input flex-1 font-mono text-sm" value={portalUrl} readOnly />
            <button
              className="btn-ghost"
              onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success('Link copiado') }}
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <div className="text-xs text-gray-500 mt-2">
            API pública: <code>GET /api/servicos/publico/{os.token_publico}</code>
          </div>
        </div>
      )}
    </div>
  )
}

function EnviarWhatsAppModal({ osId, numero, telefone, onClose }: {
  osId: string; numero: number; telefone: string; onClose: () => void
}) {
  const [fone, setFone] = useState(telefone || '')
  const [tipo, setTipo] = useState<'link' | 'pdf'>('link')
  const [formato, setFormato] = useState<'a4' | 'cupom'>('a4')
  const [mensagem, setMensagem] = useState('')
  const enviar = useMutation({
    mutationFn: async () => (await api.post(`/servicos/${osId}/enviar-whatsapp`, {
      telefone: fone || undefined, tipo, formato, mensagem: mensagem || undefined,
    })).data,
    onSuccess: (r: any) => { toast.success(`OS enviada no WhatsApp (${r.enviado_para})`); onClose() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao enviar'),
  })
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-200">
          <h2 className="font-bold text-ink-900 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" /> Enviar OS #{numero} no WhatsApp
          </h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Número do cliente</label>
            <input value={fone} onChange={e => setFone(e.target.value)} placeholder="(85) 99999-9999"
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
            <p className="text-[11px] text-ink-400 mt-1">Sem DDI assume Brasil (+55).</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Enviar como</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {(['link', 'pdf'] as const).map(t => (
                <button key={t} onClick={() => setTipo(t)}
                  className={`py-2 rounded-lg border text-sm ${tipo === t ? 'border-emerald-500 bg-emerald-50 text-emerald-800 font-semibold' : 'border-ink-200 text-ink-600'}`}>
                  {t === 'link' ? 'Link do PDF' : 'PDF anexado'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Formato</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {(['a4', 'cupom'] as const).map(f => (
                <button key={f} onClick={() => setFormato(f)}
                  className={`py-2 rounded-lg border text-sm ${formato === f ? 'border-emerald-500 bg-emerald-50 text-emerald-800 font-semibold' : 'border-ink-200 text-ink-600'}`}>
                  {f === 'a4' ? 'A4 (completo)' : 'Cupom 80mm'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Mensagem (opcional)</label>
            <textarea value={mensagem} onChange={e => setMensagem(e.target.value)} rows={2}
              placeholder="Ex: Sua bike está pronta pra retirada!"
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
          </div>
          <button onClick={() => { if (!fone.trim()) { toast.error('Informe o número'); return } enviar.mutate() }}
            disabled={enviar.isPending}
            className="w-full btn-primary !bg-emerald-600 hover:!bg-emerald-700 flex items-center justify-center gap-2 disabled:opacity-50">
            {enviar.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
            {enviar.isPending ? 'Enviando...' : 'Enviar no WhatsApp'}
          </button>
          <p className="text-[11px] text-ink-400 text-center">
            Requer o WhatsApp da filial conectado em Configurações → WhatsApp.
          </p>
        </div>
      </div>
    </div>
  )
}

function PreviewOSModal({ osId, numero, onClose }: { osId: string; numero: number; onClose: () => void }) {
  const [formato, setFormato] = useState<'a4' | 'cupom'>('a4')
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    let ativo = true
    let criada = ''
    setLoading(true)
    api.get(`/servicos/${osId}/pdf`, { params: { formato }, responseType: 'blob' })
      .then(r => {
        if (!ativo) return
        criada = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
        setUrl(criada)
      })
      .catch(() => toast.error('Erro ao gerar PDF'))
      .finally(() => { if (ativo) setLoading(false) })
    return () => { ativo = false; if (criada) URL.revokeObjectURL(criada) }
  }, [formato, osId])

  const imprimir = () => {
    try { iframeRef.current?.contentWindow?.print() }
    catch { if (url) window.open(url, '_blank') }
  }
  const baixar = () => {
    if (!url) return
    const a = document.createElement('a'); a.href = url; a.download = `OS-${String(numero).padStart(6, '0')}.pdf`; a.click()
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink-200">
          <h2 className="font-bold text-ink-900 flex items-center gap-2">
            <Printer className="w-5 h-5 text-brand-600" /> OS #{numero}
          </h2>
          <div className="flex items-center gap-1 bg-ink-100 rounded-lg p-0.5">
            {(['a4', 'cupom'] as const).map(f => (
              <button key={f} onClick={() => setFormato(f)}
                className={`px-3 py-1 rounded-md text-xs font-medium ${formato === f ? 'bg-white shadow text-ink-900' : 'text-ink-500'}`}>
                {f === 'a4' ? 'A4' : 'Cupom 80mm'}
              </button>
            ))}
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 bg-ink-100 relative overflow-hidden">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-ink-400 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Gerando...
            </div>
          )}
          {url && <iframe ref={iframeRef} src={url} title="Pré-visualização da OS" className="w-full h-full" />}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-ink-200">
          <button onClick={baixar} disabled={!url} className="btn-secondary text-sm disabled:opacity-50">Baixar PDF</button>
          <button onClick={() => url && window.open(url, '_blank')} disabled={!url} className="btn-secondary text-sm disabled:opacity-50">Abrir em nova aba</button>
          <button onClick={imprimir} disabled={!url} className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50">
            <Printer className="w-4 h-4" /> Imprimir
          </button>
        </div>
      </div>
    </div>
  )
}
function ModalVincularBike({ clienteId, atual, onClose, onSelecionar, salvando }: {
  clienteId: number; atual: number | null
  onClose: () => void; onSelecionar: (id: number | null) => void; salvando: boolean
}) {
  const { data: bikes = [], isLoading } = useQuery<any[]>({
    queryKey: ['os-bikes-cliente', clienteId],
    queryFn: async () => (await api.get('/servicos/equipamentos', { params: { cliente_id: clienteId } })).data,
  })
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Bike size={18} className="text-violet-600" /> Vincular bike
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {isLoading ? (
          <div className="text-center py-8 text-gray-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando...
          </div>
        ) : bikes.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">Cliente sem bike cadastrada.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {bikes.map((b: any) => (
              <button key={b.id} type="button" disabled={salvando}
                onClick={() => onSelecionar(b.id)}
                className={`w-full text-left rounded-lg border p-2.5 transition-colors disabled:opacity-50 ${
                  atual === b.id ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-violet-300'}`}>
                <p className="text-sm font-medium">{b.descricao}{b.cor ? ` · ${b.cor}` : ''}</p>
                {b.numero_serie && <span className="text-[11px] text-gray-500 font-mono">série {b.numero_serie}</span>}
              </button>
            ))}
          </div>
        )}
        <button type="button" disabled={salvando} onClick={() => onSelecionar(null)}
          className="w-full mt-3 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-50 border border-gray-200 disabled:opacity-50">
          Nenhuma bike (OS avulsa)
        </button>
      </div>
    </div>
  )
}
